import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {tasksApiPath} from '../utils/api-url-helpers'

const ON_DEMAND_SANDBOX_ENVIRONMENT_ID = 'github-sandbox'

interface EnvironmentTaskResponse {
  id?: string
  task?: {id?: string}
  sessions?: Array<{id?: string; environment_id?: string}>
}

interface EnvironmentBoundTaskInput {
  environmentId: string
  problemStatement: string
}

interface RepositoryEnvironmentBoundTaskInput extends EnvironmentBoundTaskInput {
  repositoryNwo: string
  baseRef?: string
  computeScope?: never
  space?: never
}

interface RepositorylessEnvironmentBoundTaskInput extends EnvironmentBoundTaskInput {
  repositoryNwo?: undefined
  baseRef?: never
  computeScope?: string
  space?: {owner: string; slug: string}
}

export type CreateEnvironmentBoundTaskInput =
  | RepositoryEnvironmentBoundTaskInput
  | RepositorylessEnvironmentBoundTaskInput

export interface EnvironmentBoundTask {
  taskId: string
  sessionId: string
  boundEnvironmentId?: string
}

export async function createEnvironmentBoundTask({
  environmentId,
  problemStatement,
  repositoryNwo,
  baseRef,
  computeScope,
  space,
}: CreateEnvironmentBoundTaskInput): Promise<EnvironmentBoundTask> {
  const repository = parseNwo(repositoryNwo)
  const response = await fetchCAPI({
    path: tasksApiPath(),
    method: 'POST',
    additionalHeaders: {Accept: 'application/json'},
    body: {
      environment_id: environmentId,
      prompt: problemStatement,
      ...(repository ? {repositories: [repository]} : {}),
      ...(repository && baseRef ? {base_ref: baseRef} : {}),
      ...(!repository && computeScope ? {compute: {scope: computeScope}} : {}),
      ...(!repository && space ? {space} : {}),
    },
  })
  const body = (await response.json()) as EnvironmentTaskResponse
  const taskId = body.id ?? body.task?.id
  if (!taskId) throw new Error('Mission Control did not return a task id for this environment task')

  const session = body.sessions?.[0]
  if (!session?.id) throw new Error('Mission Control did not create a session for this environment')

  const boundEnvironmentId = session.environment_id
  if (environmentId === ON_DEMAND_SANDBOX_ENVIRONMENT_ID && !boundEnvironmentId) {
    throw new Error('Mission Control did not bind the sandbox session to an environment')
  }

  return {taskId, sessionId: session.id, boundEnvironmentId}
}

function parseNwo(nwo: string | undefined): {owner: string; name: string} | undefined {
  if (!nwo) return undefined
  const [owner, name] = nwo.split('/')
  if (!owner || !name) return undefined
  return {owner, name}
}
